import { axios } from "..";
import type { IOfficeNew } from "../interfaces/models/IOffice";
import { sortOfficesForProfileBlocks } from "../pages/ManageFirm/Professionals/manageDataAdminOfficeRules";

export const ACCOUNTING_TABLE_OFFICE_VISIBILITY_FILTER = {
  showInDirectory: { value: true, comparisonType: "EQUAL" },
  excludeMustExclude: { value: true, comparisonType: "EQUAL" },
};

export type AccountingTableRowKind =
  | "standalone"
  | "groupParent"
  | "groupChild";

export type AccountingTableRow = Record<string, unknown> & {
  id: string;
  firmId?: string;
  firm?: string;
  jurisdiction?: string;
  membershipType?: string;
  _rowKind?: AccountingTableRowKind;
  _displayFirmId?: string;
  _hasChildren?: boolean;
  _isExpanded?: boolean;
};

export type AccountingFirmOfficeGroup = {
  firmId: string;
  parentOfficeId: string;
  childOfficeIds: string[];
  offices: IOfficeNew[];
};

export type AccountingOfficeGroupingOptions = {
  /** When true (default), only `showInDirectory` offices participate in parent/child groups. */
  requireShowInDirectory?: boolean;
};

function isGroupableOffice(
  office: IOfficeNew,
  options?: AccountingOfficeGroupingOptions,
): boolean {
  const requireShowInDirectory = options?.requireShowInDirectory ?? true;
  if (
    !Boolean(String(office.id ?? "").trim()) ||
    !Boolean(String(office.firm ?? "").trim())
  ) {
    return false;
  }
  if (!requireShowInDirectory) {
    return true;
  }
  return office.showInDirectory === true;
}

export function buildAccountingFirmOfficeGroups(
  offices: IOfficeNew[],
  options?: AccountingOfficeGroupingOptions,
): Map<string, AccountingFirmOfficeGroup> {
  const byFirm = new Map<string, IOfficeNew[]>();

  for (const office of offices) {
    if (!isGroupableOffice(office, options)) {
      continue;
    }
    const firmId = String(office.firm).trim();
    const list = byFirm.get(firmId) ?? [];
    list.push(office);
    byFirm.set(firmId, list);
  }

  const groups = new Map<string, AccountingFirmOfficeGroup>();
  for (const [firmId, firmOffices] of byFirm) {
    const sorted = sortOfficesForProfileBlocks(firmOffices);
    const parent = sorted[0];
    if (!parent?.id) {
      continue;
    }

    groups.set(firmId, {
      firmId,
      parentOfficeId: String(parent.id),
      childOfficeIds: sorted
        .slice(1)
        .map((office) => String(office.id))
        .filter(Boolean),
      offices: sorted,
    });
  }

  return groups;
}

function pageRowToOffice(row: AccountingTableRow): IOfficeNew | null {
  const id = String(row.id ?? "").trim();
  const firm = String(row.firmId ?? "").trim();
  if (!id || !firm) {
    return null;
  }

  return {
    id,
    firm,
    firmName: String(row.firm ?? ""),
    jurisdiction: String(row.jurisdiction ?? ""),
    membershipType: row.membershipType as IOfficeNew["membershipType"],
    showInDirectory: true,
    name: String(row.firm ?? ""),
    shortId: row.shortId as IOfficeNew["shortId"],
    officeType: "main",
    city: "",
    cityName: "",
    country: "",
    countryName: "",
    createdDate: 0,
  };
}

export function mergeAccountingFirmOfficeIndex(
  index: Map<string, AccountingFirmOfficeGroup>,
  pageRows: AccountingTableRow[],
  options?: AccountingOfficeGroupingOptions,
): Map<string, AccountingFirmOfficeGroup> {
  const offices: IOfficeNew[] = [];
  const seenOfficeIds = new Set<string>();

  for (const group of index.values()) {
    for (const office of group.offices) {
      const officeId = String(office.id ?? "");
      if (!officeId || seenOfficeIds.has(officeId)) {
        continue;
      }
      seenOfficeIds.add(officeId);
      offices.push(office);
    }
  }

  for (const row of pageRows) {
    const office = pageRowToOffice(row);
    if (!office?.id || seenOfficeIds.has(office.id)) {
      continue;
    }
    seenOfficeIds.add(office.id);
    offices.push(office);
  }

  return buildAccountingFirmOfficeGroups(offices, options);
}

export async function fetchAccountingFirmOfficeIndex(
  options?: AccountingOfficeGroupingOptions,
): Promise<
  Map<string, AccountingFirmOfficeGroup>
> {
  const requireShowInDirectory = options?.requireShowInDirectory ?? true;
  try {
    const params = new URLSearchParams();
    if (requireShowInDirectory) {
      params.append(
        "filter",
        JSON.stringify(ACCOUNTING_TABLE_OFFICE_VISIBILITY_FILTER),
      );
    }
    params.append(
      "fields",
      JSON.stringify([
        "id",
        "firm",
        "membershipType",
        "jurisdiction",
        "jurisdictionName",
        "firmName",
        "firmShortName",
        "name",
        "shortId",
        "showInDirectory",
      ]),
    );
    params.append(
      "pagination",
      JSON.stringify({ pageIndex: 0, pageSize: 5000 }),
    );

    const response = await axios.Get(`/office?${params.toString()}`);
    const offices = Array.isArray(response.data)
      ? (response.data as IOfficeNew[])
      : [];
    return buildAccountingFirmOfficeGroups(offices, options);
  } catch {
    return new Map();
  }
}

function applyRowKind(
  row: AccountingTableRow,
  rowKind: AccountingTableRowKind,
  hasChildren: boolean,
  isExpanded: boolean,
  displayFirmId?: string,
): AccountingTableRow {
  return {
    ...row,
    _rowKind: rowKind,
    _displayFirmId: displayFirmId,
    _hasChildren: hasChildren,
    _isExpanded: isExpanded,
  };
}

export function formatAccountingJurisdictionCell(
  row: AccountingTableRow,
  group?: AccountingFirmOfficeGroup,
): string {
  const jurisdiction = String(row.jurisdiction ?? "").trim();
  if (
    row._rowKind === "groupParent" &&
    group &&
    group.childOfficeIds.length > 0 &&
    jurisdiction
  ) {
    return `${jurisdiction}`;
  }
  return jurisdiction;
}

export function buildAccountingTableDisplayRows(
  pageRows: AccountingTableRow[],
  firmGroups: Map<string, AccountingFirmOfficeGroup>,
  expandedFirmIds: ReadonlySet<string>,
  options?: AccountingOfficeGroupingOptions,
): AccountingTableRow[] {
  if (!pageRows.length) {
    return [];
  }

  const effectiveGroups = mergeAccountingFirmOfficeIndex(
    firmGroups,
    pageRows,
    options,
  );

  const pageRowsById = new Map(
    pageRows.map((row) => [String(row.id), row] as const),
  );
  const pageIds = new Set(pageRowsById.keys());
  const output: AccountingTableRow[] = [];

  // Iterate rows in the (server-sorted) order they arrive. For multi-office
  // firms we only emit the group when we reach the PARENT office row, so the
  // parent lands at its own sorted position (matching the value shown in the
  // row). Non-parent offices are skipped here and rendered indented under the
  // parent when the firm is expanded. This keeps the visible order aligned with
  // whatever column the table is sorted by.
  //
  // When a membership (or other) filter returns only child offices, the parent
  // row is absent from `pageRows`. Those children are emitted as standalone rows
  // so filtered secondary / multiple-primary offices remain visible. When the
  // parent is present, children stay collapsed until the user expands the group.
  for (const row of pageRows) {
    const rowId = String(row.id ?? "");
    if (!rowId) {
      continue;
    }

    const firmId = String(row.firmId ?? "").trim();
    const group = firmId ? effectiveGroups.get(firmId) : undefined;

    if (!group || group.offices.length <= 1) {
      output.push(applyRowKind(row, "standalone", false, false));
      continue;
    }

    const parentInPage = pageIds.has(group.parentOfficeId);
    const isParent = rowId === group.parentOfficeId;

    if (!isParent) {
      if (!parentInPage) {
        output.push(applyRowKind(row, "standalone", false, false));
      }
      continue;
    }

    const isExpanded = expandedFirmIds.has(firmId);

    output.push(
      applyRowKind(row, "groupParent", true, isExpanded, firmId),
    );

    if (isExpanded) {
      for (const childId of group.childOfficeIds) {
        if (!pageIds.has(childId)) {
          continue;
        }
        const childRow = pageRowsById.get(childId);
        if (!childRow) {
          continue;
        }
        output.push(applyRowKind(childRow, "groupChild", false, false, firmId));
      }
    }
  }

  return output;
}
